import { type RefObject, useEffect, useRef } from 'react';

import { findCard, revealCard, scrollBehavior } from './railScroll';

/** Bioscope strips hold thousands of frames, so their thumbnails load closer in. */
const LAZY_LOAD_MARGIN = { dense: '100px', default: '600px' };

function swapInRealSource(entries: IntersectionObserverEntry[], observer: IntersectionObserver) {
    for (const entry of entries) {
        if (!entry.isIntersecting) {
            continue;
        }
        const image = entry.target.querySelector('img');
        if (image?.dataset.src && image.src !== image.dataset.src) {
            image.src = image.dataset.src;
            image.removeAttribute('data-src');
            observer.unobserve(entry.target);
        }
    }
}

/**
 * Loads a card's thumbnail once it comes within the look-ahead margin of the strip's visible
 * part. The observer's root is the scrolling strip itself: rooted on a wrapper that does not
 * scroll, the margin never applied and thumbnails only loaded once already on screen.
 *
 * One observer for the strip's lifetime; cards that arrive later are picked up as they are
 * added, so a re-render of the same cards builds nothing new.
 */
export function useLazyThumbnails(trackRef: RefObject<HTMLElement | null>, dense: boolean): void {
    useEffect(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        const observer = new IntersectionObserver(swapInRealSource, {
            root: track,
            rootMargin: dense ? LAZY_LOAD_MARGIN.dense : LAZY_LOAD_MARGIN.default,
            threshold: 0,
        });
        const watch = (nodes: Iterable<Node>) => {
            for (const node of nodes) {
                if (node instanceof HTMLElement && node.querySelector('img[data-src]')) {
                    observer.observe(node);
                }
            }
        };
        const forget = (nodes: Iterable<Node>) => {
            for (const node of nodes) {
                if (node instanceof HTMLElement) {
                    observer.unobserve(node);
                }
            }
        };
        watch(Array.from(track.children));
        const cards = new MutationObserver((records) => {
            for (const record of records) {
                forget(Array.from(record.removedNodes));
                watch(Array.from(record.addedNodes));
            }
        });
        cards.observe(track, { childList: true });
        return () => {
            cards.disconnect();
            observer.disconnect();
        };
    }, [trackRef, dense]);
}

/**
 * Keeps the current image's card in view: scrolled to when the selection moves to a card off
 * screen, and when a new day or filter replaces the cards. A strip the reader scrolled away by
 * hand stays where they left it until the selection moves again.
 */
export function useKeepCurrentCardInView(
    trackRef: RefObject<HTMLElement | null>,
    currentImageId: string | undefined
): void {
    const hasScrolled = useRef(false);
    const currentRef = useRef(currentImageId);
    currentRef.current = currentImageId;

    useEffect(() => {
        const track = trackRef.current;
        if (!track || !currentImageId || track.dataset.gesture) {
            return;
        }
        const card = findCard(track, currentImageId);
        if (card) {
            // The first reveal places the strip before it is seen, so it does not animate.
            revealCard(track, card, hasScrolled.current ? scrollBehavior() : 'auto');
            hasScrolled.current = true;
        }
    }, [trackRef, currentImageId]);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) {
            return;
        }
        const cards = new MutationObserver(() => {
            const card = currentRef.current ? findCard(track, currentRef.current) : null;
            if (card) {
                revealCard(track, card, 'auto');
            }
        });
        cards.observe(track, { childList: true });
        return () => cards.disconnect();
    }, [trackRef]);
}
